import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AppExerciseLineChart from '../../../../components/charts/app-exercise-line-chart/AppExerciseLineChart';
import AppTrainingLoadBarChart from '../../../../components/charts/app-training-load-bar-chart/AppTrainingLoadBarChart';
import AppSelectWrapper from '../../../../components/designe/textfields/app-select-wrapper/AppSelectWrapper';
import NoResults from '../../../../components/reusable/no-results';
import { IExercise } from '../../../../types/interfaces/exercise';
import { ITraining } from '../../../../types/interfaces/training';
import { formatExerciseOptionSelectorLabel } from '../../../../utils/exercise-option-display';
import { useStyles } from './ExerciseStatistics.styles';
import { useExerciseStatistics } from './useExerciseStatistics';

interface IExerciseStatisticsProps {
  exercise: IExercise;
  trainings: ITraining[];
}

const ExerciseStatistics = ({
  exercise,
  trainings,
}: IExerciseStatisticsProps) => {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const {
    selectedPeriodOption,
    periodOptions,
    isLoading,
    handlePeriodChange,
    exerciseOptions,
    selectedOption,
    handleOptionChange,
    filteredTrainings,
  } = useExerciseStatistics({ exercise, trainings });

  const optionSelectOptions = React.useMemo(() => {
    return exerciseOptions.map((opt) => ({
      id: opt.id,
      name: formatExerciseOptionSelectorLabel(
        opt.name || opt.nameKey,
        opt.unit,
        opt.unitKey,
      ),
    }));
  }, [exerciseOptions]);

  const selectedOptionForSelect = React.useMemo(() => {
    if (!selectedOption) return null;
    return {
      id: selectedOption.id,
      name: formatExerciseOptionSelectorLabel(
        selectedOption.name || selectedOption.nameKey,
        selectedOption.unit,
        selectedOption.unitKey,
      ),
    };
  }, [selectedOption]);

  return (
    <Box className={classes.overviewWrapper}>
      <Box className={classes.selectorsWrapper}>
        <Box className={classes.periodSelector}>
          <AppSelectWrapper
            label={t('statistics.periodLabel')}
            fieldName="name"
            disableSearch
            disableSorting
            disableClear
            options={periodOptions}
            defaultValue={selectedPeriodOption}
            onChange={handlePeriodChange}
            mode="dark"
          />
        </Box>

        {exerciseOptions.length > 1 && (
          <Box className={classes.optionSelector}>
            <AppSelectWrapper
              label={t('exercises.options')}
              fieldName="name"
              disableSearch
              disableSorting
              disableClear
              options={optionSelectOptions}
              defaultValue={selectedOptionForSelect}
              onChange={handleOptionChange}
              mode="dark"
            />
          </Box>
        )}
      </Box>

      {!isLoading && (
        <>
          {!selectedOption || filteredTrainings.length === 0 ? (
            <Box className={classes.emptyState}>
              <NoResults />
            </Box>
          ) : (
            <>
              <Box className={classes.chartSection}>
                <Typography variant="h3">
                  {t('exercises.exerciseProgress')}
                </Typography>
                <AppExerciseLineChart
                  trainings={filteredTrainings}
                  exerciseId={exercise.id}
                  splitBySide={exercise.splitBySide}
                  selectedOption={selectedOption}
                  height={400}
                />
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default ExerciseStatistics;
